import React, { useRef, useEffect, useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

// Placeholder: Imagem de garfo e faca cruzados (idêntica à referência do usuário) - ~1.7KB
const FOOD_PLACEHOLDER_IMG = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4QBMRXhpZgAATU0AKgAAAAgABAEAAAQAAAABAAAEOAEBAAQAAAABAAAEQAESAAMAAAABAAEAAIdpAAQAAAABAAAAPgAAAAAAAAAAAAD/4gIoSUNDX1BST0ZJTEUAAQEAAAIYAAAAAAQwAABtbnRyUkdCIFhZWiAAAAAAAAAAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAAHRyWFlaAAABZAAAABRnWFlaAAABeAAAABRiWFlaAAABjAAAABRyVFJDAAABoAAAAChnVFJDAAABoAAAAChiVFJDAAABoAAAACh3dHB0AAAByAAAABRjcHJ0AAAB3AAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAFgAAAAcAHMAUgBHAEIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFhZWiAAAAAAAABvogAAOPUAAAOQWFlaIAAAAAAAAGKZAAC3hQAAGNpYWVogAAAAAAAAJKAAAA+EAAC2z3BhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABYWVogAAAAAAAA9tYAAQAAAADTLW1sdWMAAAAAAAAAAQAAAAxlblVTAAAAIAAAABwARwBvAG8AZwBsAGUAIABJAG4AYwAuACAAMgAwADEANv/bAEMADQkKCwoIDQsKCw4ODQ8TIBUTEhITJxweFyAuKTEwLiktLDM6Sj4zNkY3LC1AV0FGTE5SU1IyPlphWlBgSlFST//bAEMBDg4OExETJhUVJk81LTVPT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT09PT//AABEIAGQAYwMBIgACEQEDEQH/xAAZAAEBAQEBAQAAAAAAAAAAAAAAAQQDAgb/xAApEAEAAgIABQMEAgMAAAAAAAAAAQIDEQQSITFBIlFhEzJCcVLhgaHB/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAH/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwD7BREVRFAEUAAARQBFBNCgAICg90pvrbtreo769weB2vj5utdc2t6jtaPeHARQBQa+Hwa1e8dfEezLad2tMdpkR5UgFBAFEAV0xW/GZ1EzuJ/jLmCNMRMzrUxO+sR+M+8fBnw+j6nTmj7tdpMGesRrJ0mI1Fvh2tMZcd647RM6UYGrh8GvXeOviHnhsUTaZv3rP2+zTe9cdea0/wBg8cRk5MU6726QwQ95Mk5Lza3+I9ngFARQQBUVAURQGjBhyRX6lZ1PiJ8rw/D79d46eIOKzd8dJ/c/8VHTpmrGTHPLeOn9Sx35+eeffNHfb1hyTjtuO3mGvJjrnpFqz18SDCkLas1tNbRqYRFURQBAFAAdeGrW2WItr4/bkA28Rm5I5az6p/0xAqDrw+b6dtT9s9/hyRFbeKik4ubcb8T7sSoIKAoIAoAAAAAIoAIoAAAAIACgAIoAIAAAKAAAD//Z";

// Componente de imagem com placeholder de garfo/faca + fade-in suave
const LazyProductImage = ({ src, alt, className }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const imageUrlRef = useRef(src);
  
  useEffect(() => {
    if (imageUrlRef.current !== src) {
      imageUrlRef.current = src;
      setIsLoaded(false);
      setHasError(false);
    }
  }, [src]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '150px' }
    );
    
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const fallbackImage = 'https://via.placeholder.com/300x300/f3f4f6/9ca3af?text=Sem+Imagem';
  
  return (
    <div ref={imgRef} className={`relative bg-gray-100 overflow-hidden ${className}`}>
      {/* Placeholder: Ícone de garfo/faca */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${isLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundColor: '#f3f4f6' }}
      >
        <img 
          src={FOOD_PLACEHOLDER_IMG} 
          alt="" 
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Imagem HD com fade-in suave */}
      <img
        src={isInView ? (hasError ? fallbackImage : src) : undefined}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-500 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => requestAnimationFrame(() => setIsLoaded(true))}
        onError={() => {
          setHasError(true);
          setIsLoaded(true);
        }}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
};

const FeaturedProductsCarousel = ({ products, onProductClick }) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [products]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8;
      const newScrollLeft = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  if (!products || products.length === 0) return null;

  return (
    <div className="bg-gray-50 py-4 px-3 md:px-4 rounded-lg mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-orange-500 fill-orange-500" />
          <h2 className="text-base md:text-lg font-semibold text-gray-800">
            Em Destaque
          </h2>
        </div>
        
        {/* Navigation arrows for desktop */}
        <div className="hidden md:flex gap-1">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`p-1.5 rounded-md transition-all ${
              canScrollLeft 
                ? 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm' 
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`p-1.5 rounded-md transition-all ${
              canScrollRight 
                ? 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm' 
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Próximo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Products scroll container */}
      <div
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className="flex gap-2.5 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {products.map((product) => (
          <div
            key={product.id}
            onClick={() => onProductClick(product)}
            className="flex-none w-[110px] sm:w-[120px] md:w-[130px] snap-start cursor-pointer group"
            data-testid={`featured-product-${product.id}`}
          >
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200">
              {/* Product image with lazy loading */}
              <div className="relative aspect-square overflow-hidden bg-gray-50">
                <LazyProductImage
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                />
                {/* TOP badge */}
                <div className="absolute top-1.5 right-1.5 bg-white/95 backdrop-blur-sm text-orange-600 px-1.5 py-0.5 rounded text-[10px] font-medium shadow-sm flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 fill-current" />
                  TOP
                </div>
              </div>
              
              {/* Product info */}
              <div className="p-2">
                <h3 className="font-medium text-gray-800 text-[11px] sm:text-xs mb-1 line-clamp-2 leading-tight min-h-[28px]">
                  {product.name}
                </h3>
                <span className="text-xs sm:text-sm font-semibold text-gray-900 block">
                  R$ {product.price?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Hide scrollbar CSS */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default FeaturedProductsCarousel;
